import SendEmailIcon from "assets/img/svg/sendEmail";
import ControlFilter from "components/shared/ControlFilter";
import type { PopoverMenuElements } from "components/shared/ElementList/Header";
import ElementListHeader from "components/shared/ElementList/Header";
import type { LastElementTableRef } from "components/shared/LastElementTable";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import SendEmailBeneficiar from "components/shared/SendEmailBeneficiar";
import { toTermBeneficiary } from "components/shared/SendEmailBeneficiar/helpers";
import SmallSearchBar from "components/shared/SmallSearchBar";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import {
    documentStatusRules,
    EXPIRING_SOON_DAYS,
    formatShortDate,
    outdatedDocumentsOfEntry,
} from "helpers/beneficiaryDocuments";
import { useAbility } from "hooks/useAbility";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    IDocumentCell,
    IDocumentsOverviewEntry,
    TCellStatus,
    TOverviewParams,
} from "store/beneficiaryDocumentsOverviewStore";
import { fetchDocumentsOverview, sendDocumentsReminder } from "store/beneficiaryDocumentsOverviewStore";
import type { Job } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TSortOrder } from "types";

import styles from "./style.module.scss";
import ViewToggle from "./ViewToggle";

interface IBeneficiaryDocumentsTable {
    /** Opens the beneficiary in the list view */
    onOpenBeneficiary: (id: number) => void;
    onSwitchToList: () => void;
    /** Same imports / exports menu as the list view, so the header is identical in both views */
    popoverMenu?: PopoverMenuElements;
    pendingJobs?: TPendingImport[] | Job[];
}

const ALL = "all";
type TQuickFilter = "all" | "outdated" | "pending" | "never";
const QUICK_FILTERS: TQuickFilter[] = ["all", "outdated", "pending", "never"];
const CELL_STATUSES: TCellStatus[] = ["missing", "valid", "expiring_soon", "expired"];

/**
 * Beneficiaries × document types table (planche 2): company summary, filters and quick
 * filters, selection with bulk reminder email, one column per active type, documents to
 * qualify and last reminder, API pagination.
 */
const BeneficiaryDocumentsTable = ({
    onOpenBeneficiary,
    onSwitchToList,
    popoverMenu,
    pendingJobs,
}: IBeneficiaryDocumentsTable) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { entries, count, types, summary, status } = useAppSelector((state) => state.beneficiaryDocumentsOverview);
    const statusRules = documentStatusRules(t);
    const tableRef = useRef<LastElementTableRef>(null);

    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<string>(ALL);
    const [statusFilter, setStatusFilter] = useState<string>(ALL);
    const [quickFilter, setQuickFilter] = useState<TQuickFilter>("all");
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(parseInt(localStorage.getItem("settings_pagination") ?? "25"));
    const [sortBy, setSortBy] = useState<TOverviewParams["sortBy"]>("name");
    const [sortOrder, setSortOrder] = useState<TSortOrder>("asc");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isMailOpen, setIsMailOpen] = useState(false);

    const params = useMemo<TOverviewParams>(
        () => ({
            offset: page * limit,
            limit,
            query: query || undefined,
            documentTypeId: typeFilter === ALL ? undefined : Number(typeFilter),
            status: statusFilter === ALL ? undefined : (statusFilter as TCellStatus),
            onlyOutdated: quickFilter === "outdated" ? "1" : undefined,
            hasPending: quickFilter === "pending" ? "1" : undefined,
            reminder: quickFilter === "never" ? "never" : undefined,
            sortBy,
            sortOrder,
        }),
        [page, limit, query, typeFilter, statusFilter, quickFilter, sortBy, sortOrder],
    );

    useEffect(() => {
        const timeout = setTimeout(() => dispatch(fetchDocumentsOverview(params)), query ? 400 : 0);
        return () => clearTimeout(timeout);
    }, [params]);

    // Back to the first page whenever a filter changes
    useEffect(() => {
        setPage(0);
    }, [query, typeFilter, statusFilter, quickFilter, limit]);

    // The selection only means something on the rows currently displayed
    useEffect(() => {
        tableRef.current?.setSelectedRows([]);
    }, [params]);

    const selectedEntries = useMemo(
        () => entries.filter((entry) => selectedIds.includes(entry.id)),
        [entries, selectedIds],
    );
    const withoutEmail = selectedEntries.filter((entry) => !entry.email);

    const ability = useAbility();
    const canRemind = ability.can("update", "Beneficiary");

    // The {{documents}} preview shows a recipient who actually has something to send
    const reminderPreview = useMemo(() => {
        const previewEntry =
            selectedEntries.find((entry) => entry.email && outdatedDocumentsOfEntry(entry.documents, types).length) ??
            selectedEntries.find((entry) => entry.email);

        return previewEntry
            ? {
                  beneficiaryName: `${previewEntry.surname ?? ""} ${previewEntry.name ?? ""}`.trim(),
                  documents: outdatedDocumentsOfEntry(previewEntry.documents, types),
              }
            : undefined;
    }, [selectedEntries, types]);

    const handleSendReminder = async ({
        subject,
        body,
        mailCc,
    }: {
        subject: string;
        body: string;
        mailCc: string[];
    }) => {
        const beneficiaryIds = selectedEntries.filter((entry) => entry.email).map((entry) => entry.id);
        if (!beneficiaryIds.length) return;
        await dispatch(sendDocumentsReminder({ beneficiaryIds, subject, body, mailCc }));
    };

    const cellLabel = (cell: IDocumentCell) => {
        if (cell.status === "valid")
            return cell.validUntil
                ? t("BeneficiaryDocuments.Table.CellValidUntil", { date: formatShortDate(cell.validUntil) })
                : t("BeneficiaryDocuments.Status.valid");
        if (cell.status === "expiring_soon" && cell.validUntil)
            return t("BeneficiaryDocuments.Table.CellExpiresOn", { date: formatShortDate(cell.validUntil) });
        if (cell.status === "expired" && cell.validUntil)
            return t("BeneficiaryDocuments.Table.CellExpiredOn", { date: formatShortDate(cell.validUntil) });

        return t(`BeneficiaryDocuments.Status.${cell.status}`);
    };

    const typeOptions = [
        { value: ALL, label: t("BeneficiaryDocuments.Table.AllTypes") },
        ...types.map((type) => ({ value: String(type.id), label: type.name })),
    ];
    const statusOptions = [
        { value: ALL, label: t("BeneficiaryDocuments.Table.AllStatuses") },
        ...CELL_STATUSES.map((cellStatus) => ({
            value: cellStatus,
            label: t(`BeneficiaryDocuments.Status.${cellStatus}`),
        })),
    ];
    const quickFilterOptions = QUICK_FILTERS.map((filter) => ({
        value: filter,
        label: t(`BeneficiaryDocuments.Table.Quick.${filter}`),
    }));

    const columns: LastElementTableColumns[] = [
        {
            id: "surname",
            label: t("BeneficiaryDocuments.Table.Beneficiary"),
            align: "left",
            sort: true,
            avatar: false,
            format: (_value: any, _id?: number, row?: IDocumentsOverviewEntry) => (
                <div className={styles.nameCell}>
                    <span className={styles.initial}>{(row?.surname ?? row?.name ?? "?").slice(0, 1)}</span>
                    <span className={styles.identity}>
                        <button type="button" className={styles.name} onClick={() => row && onOpenBeneficiary(row.id)}>
                            {row?.surname?.toUpperCase() ?? ""} {row?.name ?? ""}
                        </button>
                        {row?.email ? (
                            <span className={styles.email}>{row.email}</span>
                        ) : (
                            <span className={styles.noEmail}>{t("BeneficiaryDocuments.Table.NoEmail")}</span>
                        )}
                    </span>
                </div>
            ),
        },
        ...types.map(
            (type): LastElementTableColumns => ({
                id: `type_${type.id}`,
                label: type.name,
                align: "center",
                format: (_value: any, _id?: number, row?: IDocumentsOverviewEntry) => {
                    const cell = row?.documents[type.id];
                    if (!cell) return <span className={styles.muted}>—</span>;

                    return (
                        <div className={styles.center}>
                            <StatusChip status={cell.status} statusRules={statusRules} label={cellLabel(cell)} />
                        </div>
                    );
                },
            }),
        ),
        {
            id: "pendingCount",
            label: t("BeneficiaryDocuments.Table.ToQualify"),
            align: "center",
            sort: true,
            format: (value: any) =>
                value ? (
                    <div className={styles.center}>
                        <StatusChip
                            status="pending"
                            statusRules={statusRules}
                            label={t("BeneficiaryDocuments.Table.PendingDocuments", { count: Number(value) })}
                        />
                    </div>
                ) : (
                    <span className={styles.muted}>—</span>
                ),
        },
        {
            id: "lastDocumentReminderAt",
            label: t("BeneficiaryDocuments.Table.LastReminder"),
            align: "center",
            sort: true,
            format: (value: any) => (
                <span className={styles.dateCell}>
                    {value ? formatShortDate(value) : t("BeneficiaryDocuments.Table.Never")}
                </span>
            ),
        },
    ];

    const summaryText = summary
        ? [
              t("BeneficiaryDocuments.Table.SummaryPrefix"),
              [
                  t("BeneficiaryDocuments.Table.SummaryOutdated", { count: summary.outdated }),
                  t("BeneficiaryDocuments.Table.SummaryExpiring", {
                      count: summary.expiringSoon,
                      days: EXPIRING_SOON_DAYS,
                  }),
                  t("BeneficiaryDocuments.Table.SummaryPending", { count: summary.pending }),
              ].join(", "),
          ].join(" · ")
        : "";

    return (
        <div className={styles.wrapper}>
            <div className={styles.head}>
                <div className={styles.headRow}>
                    <div className={styles.headLeft}>
                        <div className={styles.titleRow}>
                            <ElementListHeader
                                label={t("Beneficiars.Beneficiars")}
                                count={summary?.total ?? count}
                                headerActions={<ViewToggle active onClick={onSwitchToList} />}
                                popoverMenu={popoverMenu}
                                pendingJobs={pendingJobs}
                            />
                        </div>
                        <span className={styles.subtitle}>{summaryText}</span>
                    </div>
                </div>

                <div className={styles.filters}>
                    <SmallSearchBar
                        filter={query}
                        onSearch={(e) => setQuery(e.target.value)}
                        style={{ width: 280, flexGrow: 0 }}
                    />
                    <ControlFilter
                        filterLabel={t("BeneficiaryDocuments.Table.Type")}
                        filterOptions={typeOptions}
                        controlValue={typeFilter}
                        controlLabel={typeOptions.find((option) => option.value === typeFilter)?.label}
                        onValueChange={(value) => setTypeFilter(value)}
                        selector
                        menuMaxHeight="300px"
                        highlight={typeFilter !== ALL}
                    />
                    <ControlFilter
                        filterLabel={t("BeneficiaryDocuments.Table.Status")}
                        filterOptions={statusOptions}
                        controlValue={statusFilter}
                        controlLabel={statusOptions.find((option) => option.value === statusFilter)?.label}
                        onValueChange={(value) => setStatusFilter(value)}
                        selector
                        highlight={statusFilter !== ALL}
                    />
                    <ControlFilter
                        filterOptions={quickFilterOptions}
                        controlValue={quickFilter}
                        onValueChange={(value) => setQuickFilter(value)}
                    />
                </div>
            </div>

            <div className={styles.body}>
                {selectedIds.length > 0 && (
                    <div className={styles.selection} role="region">
                        <span className={styles.selectionCount}>{selectedIds.length}</span>
                        <StyledButton
                            buttonType="primary"
                            disabled={!canRemind || selectedEntries.every((entry) => !entry.email)}
                            onClick={() => setIsMailOpen(true)}
                        >
                            <SendEmailIcon />
                            {t("BeneficiaryDocuments.Table.Remind")}
                        </StyledButton>
                        <StyledButton
                            buttonType="secondary"
                            tooltipText={t("BeneficiaryDocuments.Table.SelectOutdatedHint")}
                            onClick={() =>
                                tableRef.current?.setSelectedRows(
                                    entries.filter((entry) => entry.outdated).map((entry) => entry.id),
                                )
                            }
                        >
                            {t("BeneficiaryDocuments.Table.SelectOutdated")}
                        </StyledButton>
                        <StyledButton buttonType="secondary" onClick={() => tableRef.current?.setSelectedRows([])}>
                            {t("BeneficiaryDocuments.Table.ClearSelection")}
                        </StyledButton>
                        {withoutEmail.length > 0 && (
                            <span className={styles.noEmailWarning}>
                                {t("BeneficiaryDocuments.Table.NoEmailIgnored", { count: withoutEmail.length })}
                            </span>
                        )}
                    </div>
                )}

                <LastElementTable
                    ref={tableRef}
                    columns={columns}
                    data={entries}
                    dataCount={count}
                    max={-1}
                    stickyHeader
                    selectable
                    onSelectRows={(ids) => setSelectedIds(ids as number[])}
                    isLoading={status === "pending" && entries.length === 0}
                    dataType="beneficiaries"
                    tableClassName={styles.table}
                    APIData={{
                        count,
                        page,
                        limitPagination: limit,
                        handleChangePage: (_event, newPage) => setPage(newPage),
                        handleChangeLimit: (event) => setLimit(parseInt(event.target.value, 10)),
                        handleSort: (name: string) => {
                            const key = name === "surname" ? "name" : (name as TOverviewParams["sortBy"]);
                            if (key === sortBy) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                            else {
                                setSortBy(key);
                                setSortOrder("asc");
                            }
                        },
                        sortBy: sortBy === "name" ? "surname" : sortBy,
                        sortOrder,
                        requestStatus: status,
                    }}
                />
            </div>

            {isMailOpen && (
                <SendEmailBeneficiar
                    isModalOpen={isMailOpen}
                    onClose={() => setIsMailOpen(false)}
                    beneficiaries={selectedEntries.map(toTermBeneficiary)}
                    hasCustomStatementsBeneficiars={[]}
                    haveNoEmail={withoutEmail.map(toTermBeneficiary)}
                    periodId={undefined}
                    isBulk={selectedEntries.length > 1}
                    documentsReminder={{ onSend: handleSendReminder, preview: reminderPreview }}
                />
            )}
        </div>
    );
};

export default BeneficiaryDocumentsTable;
